import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

// Import Font Awesome icons from react-icons/fa
import { FaLaptopCode, FaRobot, FaChartBar, FaUsers } from 'react-icons/fa';

/*
  1) Section container with a dark background.
  2) Mobile-first approach: less padding on small devices,
     more generous padding on desktops.
*/
const WhySkhoolSection = styled.section`
  padding: 3rem 1rem;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
  color: #0f172a;
  text-align: center;
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

/*
  SectionTitle:
  - Smaller font for mobile, larger on bigger screens
  - Light accent color #bbdefb stands out on dark #1a237e
*/
const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1d4ed8;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: 2.4rem;
    margin-bottom: 1.5rem;
  }
`;

/*
  SectionDescription:
  - Adjust for readability on dark background
  - Tighter line-height & margin on mobile, more relaxed on desktop
*/
const SectionDescription = styled.p`
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
  max-width: 700px;
  margin: 0 auto 2rem;

  @media (min-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 3rem;
  }
`;

/*
  FeaturesScrollContainer:
  - Horizontal scroll container with smaller gap
  - Overflow hidden
  - On desktop, we keep the same approach but with slightly more spacing
*/
const FeaturesScrollContainer = styled.div`
  display: flex;
  gap: 2rem;
  padding-bottom: 1rem;
  max-width: 85%;
  margin: 0 auto;
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    gap: 2.5rem;
    padding-bottom: 2rem;
  }
`;

/*
  FeatureBox:
  - Slightly smaller width on mobile
  - Scales up on larger screens
  - Still keeps the hover effect & card design
*/
const FeatureBox = styled.div`
  flex: 0 0 auto;
  width: 260px;
  padding: 1.5rem;
  background: linear-gradient(160deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: left;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
  }

  @media (min-width: 768px) {
    width: 320px;
    padding: 2rem;
  }
`;

/*
  FeatureIcon:
  - Slightly smaller size on mobile, bigger on desktop
  - Contrasting color (#4fc3f7) on the dark background
*/
const FeatureIcon = styled.div`
  font-size: 2.2rem;
  color: #0ea5a4;
  margin-bottom: 1.2rem;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 3rem;
    margin-bottom: 2rem;
  }
`;

/*
  FeatureTitle:
  - Keep text compact on mobile
  - Slightly larger on desktop
*/
const FeatureTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.6rem;

  @media (min-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

/*
  FeatureDescription:
  - Smaller size on mobile, bigger on desktop
  - Enough line-height for readability
*/
const FeatureDescription = styled.p`
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.4;

  @media (min-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const WhySkhool = () => {
  const featuresScrollRef = useRef(null);
  const animationFrameRef = useRef(null);
  const scrollSpeed = 0.5; // Adjust for faster/slower scroll
  const isHovered = useRef(false);

  // Original features with React Icons
  const features = [
    {
      title: 'Embrace the Digital Classroom',
      description:
        'Step into the future of education with Skhool. Transform your institution into a dynamic digital learning environment, preparing students for the AI-driven world.',
      icon: <FaLaptopCode />, // Use FaLaptopCode component
    },
    {
      title: 'AI-Powered Efficiency',
      description:
        'Leverage the power of AI to automate administrative tasks, from attendance tracking to report generation, freeing up educators to focus on teaching.',
      icon: <FaRobot />, // Use FaRobot component
    },
    {
      title: 'Data-Driven Insights',
      description:
        'Make informed decisions with real-time analytics and comprehensive reporting. Understand student performance at a glance.',
      icon: <FaChartBar />, // Use FaChartBar component
    },
    {
      title: 'Seamless Communication Hub',
      description:
        'Foster a connected school community with integrated communication tools. Enhance collaboration between teachers, parents, and students in a secure environment.',
      icon: <FaUsers />, // Use FaUsers component
    },
  ];

  // Duplicate features for infinite scroll effect
  const loopedFeatures = [...Array(2)].flatMap(() =>
    features.map((feature) => ({ ...feature }))
  );

  useEffect(() => {
    const container = featuresScrollRef.current;
    if (!container) return;

    let currentScrollPos = 0;

    const animateScroll = () => {
      if (!isHovered.current) {
        currentScrollPos += scrollSpeed;
        container.scrollLeft = currentScrollPos;

        // Infinite scroll logic
        if (currentScrollPos >= container.scrollWidth - container.offsetWidth) {
          currentScrollPos = 0; // Reset to start
        } else if (currentScrollPos < 0) {
          currentScrollPos = container.scrollWidth - container.offsetWidth;
        }
      }
      animationFrameRef.current = requestAnimationFrame(animateScroll);
    };

    animationFrameRef.current = requestAnimationFrame(animateScroll);

    const handleMouseEnter = () => {
      isHovered.current = true;
    };

    const handleMouseLeave = () => {
      isHovered.current = false;
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [scrollSpeed]);

  return (
    <WhySkhoolSection id="features">
      <SectionTitle>Step into the Digital AI World with Skhool.in</SectionTitle>
      <SectionDescription>
        In an era defined by rapid technological advancement and the rise of
        Artificial Intelligence, transforming your educational institution is
        not just an option—it's a necessity. Skhool provides the tools to seamlessly
        integrate into this digital future, ensuring your students are prepared and
        your institution thrives.
      </SectionDescription>

      <FeaturesScrollContainer ref={featuresScrollRef}>
        {loopedFeatures.map((feature, index) => (
          <FeatureBox key={index}>
            <FeatureIcon>{feature.icon}</FeatureIcon> {/* Render the icon component */}
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureBox>
        ))}
      </FeaturesScrollContainer>
    </WhySkhoolSection>
  );
};

export default WhySkhool;
